import { Box, CircularProgress } from '@mui/material'
import { useContext } from 'react'
import { createPortal } from 'react-dom'
import { ContentAreaContext } from 'src/layouts/ContentAreaContext'
import ProgressBar from './ProgressBar'

// ----------------------------------------------------------------------
// Waiting for one screen, not for the application.
//
// This covers the content column and nothing else. The sidebar, the top bar and
// the page the operator is already looking at stay exactly where they are, so
// moving between screens is a change of content rather than the whole window
// blinking. `LoadingScreen` is the other one: it is the boot screen, it covers
// the viewport, and it is only correct before there is any chrome to keep.
//
// It renders into the content column named by `ContentAreaContext`, wherever in
// the tree it was written. Relying on the nearest positioned ancestor does not
// work here: the theme makes every `MuiCard` `position: relative` with
// `overflow: hidden`, so a loading state written inside a card was trapped in
// the card. Outside the application shell there is no content column and it
// renders where it stands, filling the initial containing block, which is what
// the screens with no chrome want.
// ----------------------------------------------------------------------

const ContentLoading = () => {
  const contentArea = useContext(ContentAreaContext)

  const overlay = (
    <Box
      // The sweep in `scripts/previewShots.mjs` waits for this to go before it
      // takes a picture, so a screenshot is never of the loader.
      data-content-loading=""
      aria-busy="true"
      sx={{
        position: 'absolute',
        inset: 0,
        // Above everything the screen draws, below everything the shell does.
        // Screens float their own furniture — the map's controls at 10, the
        // booking calendar's sticky rows at 7 and 8, the fleet task cover at 99
        // — so a low value here would let pieces of a half-loaded screen show
        // through the thing that is meant to be hiding it. The top bar sits at
        // `appBar` and the sidebar above that, and both stay clear.
        zIndex: (theme) => theme.zIndex.appBar - 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Opaque, because the screen underneath is mid-render: a table with no
        // rows yet reads as an empty fleet rather than as a wait.
        bgcolor: 'background.default',
      }}
    >
      {/*
        The name lives on the progress indicator, which already carries the
        `progressbar` role. An `aria-label` on the bare container would name an
        element that has no role to hang a name on.
      */}
      <CircularProgress aria-label="Loading" />
    </Box>
  )

  return (
    <>
      <ProgressBar />
      {contentArea ? createPortal(overlay, contentArea) : overlay}
    </>
  )
}

export default ContentLoading
